import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  ContratoCreateInput,
  ContratoCreateResponse,
  ContratoDetail,
  ContratoUpdateInput,
  EstatusContrato,
  PagedContratos,
  RegenerarPasswordResponse,
} from "./schemas";

const KEY = "contratos";

export type ContratosFilters = {
  page?: number;
  size?: number;
  search?: string;
  cliente_id?: string;
  router_id?: string;
  estatus?: EstatusContrato | "";
  incluir_cancelados?: boolean;
};

function buildParams(f: ContratosFilters): Record<string, string | number | boolean> {
  const p: Record<string, string | number | boolean> = { page: f.page ?? 1, size: f.size ?? 20 };
  if (f.search) p.search = f.search;
  if (f.cliente_id) p.cliente_id = f.cliente_id;
  if (f.router_id) p.router_id = f.router_id;
  if (f.estatus) p.estatus = f.estatus;
  if (f.incluir_cancelados) p.incluir_cancelados = true;
  return p;
}

/** Invalida contratos + resumen operativo de clientes (F14.4). */
function invalidateContratoCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [KEY] });
  qc.invalidateQueries({ queryKey: ["clientes", "resumen"] });
}

export function useListContratos(filters: ContratosFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () =>
      apiCall<PagedContratos>("get", "/api/v1/contratos", { searchParams: buildParams(filters) }),
    placeholderData: (prev) => prev,
  });
}

export function useContrato(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<ContratoDetail>("get", `/api/v1/contratos/${id}`),
    enabled: !!id,
  });
}

export function useCreateContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ContratoCreateInput) =>
      apiCall<ContratoCreateResponse>("post", "/api/v1/contratos", { json: data }),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export function useUpdateContrato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ContratoUpdateInput) =>
      apiCall<ContratoJobResult>("patch", `/api/v1/contratos/${id}`, { json: data }),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

/** F16: Response de endpoints que encolan (suspender/reactivar/cancelar/reintentar). */
export type ContratoJobResult = {
  contrato: ContratoDetail;
  job_id: string | null;
};

export function useSuspenderContrato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string | null) =>
      apiCall<ContratoJobResult>("post", `/api/v1/contratos/${id}/suspender`, {
        json: motivo ? { motivo } : {},
      }),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export function useReactivarContrato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string | null) =>
      apiCall<ContratoJobResult>("post", `/api/v1/contratos/${id}/reactivar`, {
        json: motivo ? { motivo } : {},
      }),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export type CancelarContratoArgs = {
  motivo: string;
  /** F92: si el equipo es comodato y el técnico ya lo recogió en este momento. */
  equipo_recuperado_ahora?: boolean;
};

export function useCancelarContrato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: CancelarContratoArgs | string) => {
      const payload =
        typeof args === "string"
          ? { motivo: args, equipo_recuperado_ahora: false }
          : { motivo: args.motivo, equipo_recuperado_ahora: args.equipo_recuperado_ahora ?? false };
      return apiCall<ContratoJobResult>("post", `/api/v1/contratos/${id}/cancelar`, {
        json: payload,
      });
    },
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export type MoverContratoArgs = {
  nuevo_router_id: string;
  nueva_red_id: string;
  motivo: string;
};

export type MoverContratoResult = {
  contrato: ContratoDetail;
  job_delete_id: string | null;
  job_create_id: string | null;
};

/** rc82 — Cambio de domicilio: reasignar contrato a otro router + segmento. */
export function useMoverContrato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: MoverContratoArgs) =>
      apiCall<MoverContratoResult>("post", `/api/v1/contratos/${id}/mover`, { json: args }),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

// rc107 — Edición unificada: campos + mover de router + cambiar IP en UN submit.
export type EdicionUnificadaArgs = {
  update: ContratoUpdateInput;
  /** Enviar SIEMPRE junto con nueva_red_id cuando hay traslado (aunque el
   * router sea el mismo y solo cambie el segmento — regla del backend). */
  nuevo_router_id?: string | null;
  nueva_red_id?: string | null;
  /** Solo aplica sin traslado (misma red). Al mover, la IP sale del pool destino. */
  nueva_ip?: string | null;
};

export type EdicionUnificadaResult = {
  contrato: ContratoDetail;
  /** Jobs encolados en orden (delete viejo → create/reconcile nuevo). */
  job_ids: string[];
};

export function useEditarUnificado(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: EdicionUnificadaArgs) =>
      apiCall<EdicionUnificadaResult>(
        "post", `/api/v1/contratos/${id}/editar-unificado`, { json: args },
      ),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

/** rc82 — Cambiar la IP asignada del contrato (misma red/router). */
export function useCambiarIp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { nueva_ip: string; motivo: string }) =>
      apiCall<ContratoJobResult>("post", `/api/v1/contratos/${id}/cambiar-ip`, { json: args }),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

/** rc90 — Re-anclar ciclo de facturación (nuevo corte = día del pago, cap 28).
 * Backend valida rol administrador/superadmin. Limpia la revisión pendiente. */
export function useReanclarCiclo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contratoId, dia, fechaPago, motivo }: {
      contratoId: string; dia: number; fechaPago?: string; motivo?: string;
    }) =>
      apiCall<ContratoDetail>("post", `/api/v1/contratos/${contratoId}/reanclar-ciclo`, {
        json: {
          dia,
          ...(fechaPago ? { fecha_pago: fechaPago } : {}),
          ...(motivo ? { motivo } : {}),
        },
      }),
    onSuccess: () => {
      invalidateContratoCaches(qc);
      qc.invalidateQueries({ queryKey: ["cobranza"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** rc90 — Descartar la revisión de ciclo pendiente (conserva el corte). */
export function useDescartarRevisionCiclo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contratoId: string) =>
      apiCall<ContratoDetail>(
        "post", `/api/v1/contratos/${contratoId}/reanclar-ciclo/descartar`, { json: {} },
      ),
    onSuccess: () => {
      invalidateContratoCaches(qc);
      qc.invalidateQueries({ queryKey: ["cobranza"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useMarcarEquipoRecuperado(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notas?: string) =>
      apiCall<{ contrato: ContratoDetail }>(
        "post",
        `/api/v1/contratos/${id}/equipo-recuperado`,
        { json: notas ? { notas } : {} },
      ),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

// rc118 — Mover de domicilio: la ONU cambia de PON/OLT (misma serie).
export type OnuMoverArgs = {
  motivo: string;
  olt_id: string;
  template_id?: string;
  line_profile?: string;
  vlan?: number;
  vlan_aprov?: number;
};

export type OnuMoverResult = {
  contrato: ContratoDetail;
  /** true = pre-registrada esperando conexión; false = aprovisionada al instante. */
  esperando: boolean;
  olt: string | null;
  aviso: string | null;
};

export function useMoverOnu(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: OnuMoverArgs) =>
      apiCall<OnuMoverResult>(
        "post", `/api/v1/contratos/${id}/mover-onu`, { json: args },
      ),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

// rc117 — Reemplazo de ONU (equipo quemado/dañado) en contrato de fibra.
export type OnuReemplazoArgs = {
  motivo: string;
  byteolt: {
    serial: string;
    template_id?: string;
    line_profile?: string;
    vlan?: number;
    vlan_aprov?: number;
    ya_aprovisionada?: boolean;
    pre_registro?: boolean;
    olt_id?: string;
  };
  byteacs?: { serial: string; wifi_pass?: string };
  onu_mac?: string | null;
};

export type OnuReemplazoResult = {
  contrato: ContratoDetail;
  pppoe_user: string;
  pppoe_password_plain: string;
  pre_registro_esperando: boolean;
  avisos: string[];
};

export function useReemplazarOnu(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: OnuReemplazoArgs) =>
      apiCall<OnuReemplazoResult>(
        "post", `/api/v1/contratos/${id}/reemplazar-onu`, { json: args },
      ),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export function useRegenerarPassword(id: string) {
  return useMutation({
    mutationFn: () =>
      apiCall<RegenerarPasswordResponse>("post", `/api/v1/contratos/${id}/regenerar-password`, {
        json: {},
      }),
  });
}

/** F15/F16: Encola reconciliación del contrato en el router. */
// rc97 — reintento masivo de contratos en error
export type ReintentarErroresOmitido = {
  contrato_id: string;
  codigo?: string | null;
  motivo: string;
};

export type ReintentarErroresResult = {
  encolados: number;
  codigos_encolados: string[];
  omitidos: ReintentarErroresOmitido[];
};

export function useReintentarErrores() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contrato_ids: string[] }) =>
      apiCall<ReintentarErroresResult>(
        "post", "/api/v1/contratos/reintentar-errores", { json: input },
      ),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export function useReintentarProvisionar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiCall<ContratoJobResult>(
        "post", `/api/v1/contratos/${id}/reintentar-provisionar`, { json: {} },
      ),
    onSuccess: () => invalidateContratoCaches(qc),
  });
}

export type GenerarFacturaContratoInput = {
  periodo_inicio?: string | null;  // YYYY-MM-DD
  periodo_fin?: string | null;
  fecha_vencimiento?: string | null;
  descuento_pct?: string | null;  // decimal como string
};

/**
 * Devuelve el FacturaResponse completo del backend al emitir. Acepta
 * overrides opcionales; si no se pasan, el motor usa defaults del contrato.
 */
export function useGenerarFacturaContrato(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GenerarFacturaContratoInput = {}) => {
      const clean: Record<string, unknown> = {};
      if (payload.periodo_inicio) clean.periodo_inicio = payload.periodo_inicio;
      if (payload.periodo_fin) clean.periodo_fin = payload.periodo_fin;
      if (payload.fecha_vencimiento) clean.fecha_vencimiento = payload.fecha_vencimiento;
      if (payload.descuento_pct !== undefined && payload.descuento_pct !== null && payload.descuento_pct !== "") {
        clean.descuento_pct = payload.descuento_pct;
      }
      return apiCall<import("@/features/facturas/schemas").FacturaResponse>(
        "post",
        `/api/v1/contratos/${id}/generar-factura`,
        { json: clean },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["estado-cuenta"] });
    },
  });
}
